import { MediaTimeRange } from 'media-chrome';
import {
  findMomentNear,
  getMomentLabel,
  toleranceSecondsForBar,
} from '../utils/moments.ts';

const ATTR_MOMENTS = 'moments';
const ATTR_HEATMAP = 'heatmap';

export class BunnyMediaTimeRange extends MediaTimeRange {
  #heatmapAttr;
  _heatmapData;
  #resizeObserver;
  #heatmapClipRect;
  #progressObserver;
  #scrubPreviewActive = false;
  static get observedAttributes() {
    return [...super.observedAttributes, ATTR_MOMENTS, ATTR_HEATMAP];
  }

  constructor() {
    super();

    this._moments = [];

    const containerEl = this.shadowRoot?.getElementById('container');
    if (containerEl && !this.shadowRoot.getElementById('moments')) {
      this.#injectStyles();
      this.#createMomentsOverlay(containerEl);
      this.#createHeatmap(containerEl);
      this.#ensureMomentPreviewSlot();
    }

    this.addEventListener('pointermove', (e) => this.#onScrubMove(e));
  }

  #onPointerEnd = (evt) => {
    if (evt.pointerType !== 'mouse' && evt.isPrimary) this.#endScrubPreview();
  };

  #injectStyles() {
    const style = document.createElement('style');
    style.textContent = `
        :host {
          --media-range-track-height: 4px;
          --media-range-thumb-opacity: 0; /* hide thumb */
          --media-range-track-background: rgba(0 0 0 / 0.4);
          --media-range-track-pointer-background: transparent;
        }

        #moments { position: absolute; inset: 0; pointer-events: none; z-index: 2; }
        #moments .sp__moment {
          border: 2px solid #ffffff77;
          border-radius: 50%;
          width: 12px;
          height: 12px;
          position: absolute;
          transform: translateX(-50%);
          left: 0;
          box-sizing: border-box;
          background-color: #ffffff77;
          box-shadow: 0 0 3px #00000055;
          pointer-events: all;
        }
        #moments .sp__moment:hover {
          cursor: pointer;
          border-color: #fff;
          background-color: #ffffff99;
        }

        /* Looks like the chapter display beside it (see media-time-range's own styles) */
        #moment-label {
          font-size: var(--media-font-size, 13px);
          line-height: 17px;
          min-width: 0;
          background: var(--media-preview-chapter-background, var(--_preview-background));
          border-radius: var(--media-preview-chapter-border-radius, var(--media-preview-border-radius));
          padding: var(--media-preview-chapter-padding, 3.5px 9px);
          margin: var(--media-preview-chapter-margin, 0 0 5px);
          text-shadow: var(--media-preview-chapter-text-shadow, 0 0 4px rgb(0 0 0 / .75));
        }
        :host([mediapreviewimage]) #moment-label {
          border-radius: var(--media-preview-chapter-border-radius, 0);
          padding: var(--media-preview-chapter-padding, 3.5px 9px 0);
          margin: var(--media-preview-chapter-margin, 0);
          min-width: 100%;
        }
        #moment-label[hidden] { display: none; }
        /* Heatmap layer (behind bar, configurable size/offset) */
        #heatmap {
          display: var(--media-heatmap-display, block);
          position: absolute;
          left: var(--media-heatmap-left, 0);
          width: calc(100% + var(--media-heatmap-extra-width, 0px));
          top: var(--media-heatmap-top, -13px);
          height: var(--media-heatmap-height, 30px);
          pointer-events: none;
        }
      `;
    this.shadowRoot.appendChild(style);
  }

  #createMomentsOverlay(containerEl) {
    const overlay = document.createElement('div');
    overlay.id = 'moments';
    containerEl.appendChild(overlay);
  }

  #createHeatmap(containerEl) {
    const heatmap = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'svg',
    );
    heatmap.setAttribute('id', 'heatmap');
    heatmap.setAttribute('width', '100%');
    heatmap.setAttribute('height', '100%');
    heatmap.setAttribute('preserveAspectRatio', 'none');
    if (this.appearance) {
      this.appearance.parentNode.insertBefore(heatmap, this.appearance);
    } else {
      containerEl.appendChild(heatmap);
    }
  }

  #ensureMomentPreviewSlot() {
    const previewSlot = this.shadowRoot.querySelector('slot[name="preview"]');
    if (previewSlot && !this.shadowRoot.querySelector('#moment-label')) {
      // Not a media-preview-chapter-display: media-chrome finds every one of those under
      // a controller and writes the previewed chapter into it, which would replace the
      // moment's label. A media-text-display is not a media state receiver.
      const momentLabel = document.createElement('media-text-display');
      momentLabel.id = 'moment-label';
      momentLabel.hidden = true;
      // The marker itself is announced ("Jump to: ..."); this is the same text, visually
      momentLabel.setAttribute('aria-hidden', 'true');
      const chapterDisplay = this.shadowRoot.querySelector(
        'media-preview-chapter-display',
      );
      if (chapterDisplay && chapterDisplay.parentNode === previewSlot) {
        chapterDisplay.after(momentLabel);
      } else {
        previewSlot.appendChild(momentLabel);
      }
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback?.(name, oldValue, newValue);
    if (oldValue === newValue) return;

    if (name === ATTR_MOMENTS) {
      try {
        this._moments = JSON.parse(newValue || '[]') || [];
      } catch (_e) {
        this._moments = [];
      }
      this.#renderMoments();
    }
    if (name === ATTR_HEATMAP) {
      this.#heatmapAttr = newValue || '';
      this.#applyHeatmapAttr();
    }

    // Re-render when timeline scale changes
    if (name === 'mediaduration' || name === 'mediaseekable') {
      this.#renderMoments();
      if (this.#heatmapAttr) this.#applyHeatmapAttr();
      this.#updateHeatmapClip();
    }
  }

  set moments(value) {
    this.setAttribute(ATTR_MOMENTS, JSON.stringify(value || []));
  }

  get moments() {
    return this._moments;
  }

  connectedCallback() {
    super.connectedCallback?.();
    for (const type of ['pointerup', 'pointercancel']) {
      globalThis.addEventListener(type, this.#onPointerEnd);
    }
    this.#renderMoments();
    // Smoothly keep heatmap clip in sync while playing
    // keep clip in sync with progress width
    this.#attachProgressObserver();
    // Redraw heatmap on resize
    const containerEl = this.shadowRoot?.getElementById('container');
    if (!this.#resizeObserver && containerEl) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this._heatmapData) {
          // Re-apply last dataset to recompute points for new size
          this.heatmapData = this._heatmapData;
        }
      });
      this.#resizeObserver.observe(containerEl);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    for (const type of ['pointerup', 'pointercancel']) {
      globalThis.removeEventListener(type, this.#onPointerEnd);
    }
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#progressObserver?.disconnect();
    this.#progressObserver = null;
  }

  get keysUsed() {
    return [];
  }

  /** The time span the bar covers, which markers are placed against; null until known. */
  #timelineBounds() {
    const start = Number.isFinite(this.mediaSeekableStart)
      ? this.mediaSeekableStart
      : 0;
    const end = Number.isFinite(this.mediaDuration)
      ? this.mediaDuration
      : this.mediaSeekableEnd;
    return Number.isFinite(end) && end > start ? { start, end } : null;
  }

  #renderMoments() {
    const overlay = this.shadowRoot?.getElementById('moments');
    if (!overlay) return;

    overlay.textContent = '';

    if (!Array.isArray(this._moments) || this._moments.length === 0) return;

    const bounds = this.#timelineBounds();
    if (!bounds) return;
    const { start: startTime, end: endTime } = bounds;

    // Position markers horizontally by percentage; vertically at track center
    const track = this.shadowRoot?.getElementById('track');
    const containerEl = this.shadowRoot?.getElementById('container');
    const trackRect = track?.getBoundingClientRect();
    const contRect = containerEl?.getBoundingClientRect();
    const topPx = trackRect && contRect
      ? (trackRect.top - contRect.top + trackRect.height / 2)
      : null;

    for (const m of this._moments) {
      const ts = Number(m?.timestamp);
      if (!Number.isFinite(ts)) continue;
      const clamped = Math.max(startTime, Math.min(endTime, ts));
      const ratio = (clamped - startTime) / (endTime - startTime);

      const el = document.createElement('div');
      el.className = 'sp__moment';
      el.style.left = `${ratio * 100}%`;
      if (topPx != null) el.style.top = `${topPx - 6}px`;
      const label = getMomentLabel(m);
      if (el.removeAttribute) el.removeAttribute('title');
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      if (label) el.setAttribute('aria-label', `Jump to: ${label}`);

      const seek = () => {
        this.dispatchEvent(
          new CustomEvent('mediaseekrequest', {
            bubbles: true,
            composed: true,
            detail: clamped,
          }),
        );
      };
      el.addEventListener('click', seek);
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          seek();
        }
      });

      // Show moment label only when hovering/focusing the marker itself
      el.addEventListener(
        'pointerenter',
        () => this.#showMomentPreview(label),
      );
      el.addEventListener('pointerleave', () => this.#clearMomentPreview());
      el.addEventListener('focus', () => this.#showMomentPreview(label));
      el.addEventListener('blur', () => this.#clearMomentPreview());

      overlay.appendChild(el);
    }
  }

  // Public API to update heatmap data as an array [timestamps[], values[]]
  set heatmapData(data) {
    const svg = this.shadowRoot?.getElementById('heatmap');
    if (!svg || !data || !Array.isArray(data) || data.length !== 2) return;
    const [xs, ys] = data;
    if (
      !Array.isArray(xs) || !Array.isArray(ys) || xs.length !== ys.length ||
      xs.length < 2
    ) return;

    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const rect = svg.getBoundingClientRect();
    const width = rect.width || this.container?.clientWidth || 0;
    const height = rect.height || 40;
    if (!width || !height) return;

    const minX = xs[0];
    const maxX = xs[xs.length - 1];
    const maxY = Math.max(1, ...ys);

    const points = xs.map((x, i) => {
      const px = ((x - minX) / (maxX - minX)) * width;
      const py = height - (ys[i] / maxY) * height;
      return `${px},${py}`;
    }).join(' ');

    // Gradients and clip path
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    const baseGrad = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'linearGradient',
    );
    baseGrad.setAttribute('id', 'heatmap-base-grad');
    baseGrad.setAttribute('x2', '0%');
    baseGrad.setAttribute('y2', '100%');
    const makeStop = (offset, color) => {
      const s = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      s.setAttribute('offset', offset);
      s.setAttribute('stop-color', color);
      return s;
    };
    baseGrad.append(
      makeStop('0%', '#abababaf'),
      makeStop('30%', '#ababab8f'),
      makeStop('70%', '#ababab5f'),
      makeStop('100%', '#ababab33'),
    );

    const activeGrad = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'linearGradient',
    );
    activeGrad.setAttribute('id', 'heatmap-active-grad');
    activeGrad.setAttribute('x2', '0%');
    activeGrad.setAttribute('y2', '100%');
    const mkActiveStop = (offset, opacity) => {
      const s = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      s.setAttribute('offset', offset);
      s.setAttribute(
        'stop-color',
        getComputedStyle(this).getPropertyValue('--media-range-bar-color')
          .trim() || '#ffffff',
      );
      s.setAttribute('stop-opacity', String(opacity));
      return s;
    };
    activeGrad.append(
      mkActiveStop('0%', 0.69),
      mkActiveStop('30%', .56),
      mkActiveStop('70%', .25),
      mkActiveStop('0%', 0.69),
      mkActiveStop('30%', 0.56),
      mkActiveStop('70%', 0.25),
      mkActiveStop('100%', 0.07),
    );
    // ClipPath for played progress (created in defs as well)
    const clip = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'clipPath',
    );
    clip.setAttribute('id', 'heatmap-progress-clip');
    const clipRect = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'rect',
    );
    clipRect.setAttribute('x', '0');
    clipRect.setAttribute('y', '0');
    clipRect.setAttribute('height', String(height));
    clipRect.setAttribute('width', '0');
    clip.appendChild(clipRect);

    defs.append(baseGrad, activeGrad, clip);
    svg.appendChild(defs);

    // Paths
    const first = points.split(' ')[0] || `0,${height}`;
    const last = points.split(' ').slice(-1)[0] || `${width},${height}`;
    const d = `M ${first} L ${points} L ${last.split(',')[0]},${height} L ${
      first.split(',')[0]
    },${height} Z`;

    const basePath = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path',
    );
    basePath.setAttribute('d', d);
    basePath.setAttribute('fill', 'url(#heatmap-base-grad)');
    svg.appendChild(basePath);

    const activePath = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path',
    );
    activePath.setAttribute('d', d);
    activePath.setAttribute('fill', 'url(#heatmap-active-grad)');
    activePath.setAttribute('clip-path', 'url(#heatmap-progress-clip)');
    svg.appendChild(activePath);
    this.#heatmapClipRect = clipRect;
    this.#updateHeatmapClip();

    // Base stroke (gray)
    const baseStroke = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'polyline',
    );
    baseStroke.setAttribute('points', points);
    baseStroke.setAttribute('fill', 'none');
    baseStroke.setAttribute('stroke', '#bdbdbd');
    baseStroke.setAttribute('stroke-width', '2');
    baseStroke.setAttribute('opacity', '0.6');
    svg.appendChild(baseStroke);

    // Active stroke (primary) clipped to progress
    const activeStroke = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'polyline',
    );
    activeStroke.setAttribute('points', points);
    activeStroke.setAttribute('fill', 'none');
    activeStroke.setAttribute(
      'stroke',
      getComputedStyle(this).getPropertyValue('--media-range-bar-color')
        .trim() || '#ffffff',
    );
    activeStroke.setAttribute('stroke-width', '2');
    activeStroke.setAttribute('opacity', '0.9');
    activeStroke.setAttribute('clip-path', 'url(#heatmap-progress-clip)');
    svg.appendChild(activeStroke);

    this._heatmapData = [xs, ys];
  }

  #applyHeatmapAttr() {
    const raw = this.#heatmapAttr;
    if (!raw) return;
    // CSV values only; build timestamps across duration
    const values = raw.split(',').map((v) => parseFloat(v.trim())).filter((v) =>
      Number.isFinite(v)
    );
    if (!values.length) return;
    const duration = Number.isFinite(this.mediaDuration)
      ? this.mediaDuration
      : this.mediaSeekableEnd;
    if (!Number.isFinite(duration) || duration <= 0) return;
    const xs = values.map((_, i) => (i / (values.length - 1)) * duration);
    this.heatmapData = [xs, values];
  }

  #updateHeatmapClip() {
    const svg = this.shadowRoot?.getElementById('heatmap');
    const progressEl = this.shadowRoot?.getElementById('progress');
    if (!svg || !this.#heatmapClipRect || !progressEl) return;
    const style = getComputedStyle(progressEl);
    const w = style.width;
    let px = 0;
    if (w.endsWith('px')) px = parseFloat(w);
    else if (w.endsWith('%')) {
      px = (parseFloat(w) / 100) * svg.getBoundingClientRect().width;
    } else px = progressEl.getBoundingClientRect().width;
    this.#heatmapClipRect.setAttribute('width', String(Math.max(0, px)));
  }

  #attachProgressObserver() {
    const progressEl = this.shadowRoot?.getElementById('progress');
    if (!progressEl) return;
    if (this.#progressObserver) this.#progressObserver.disconnect();
    this.#progressObserver = new ResizeObserver(() =>
      this.#updateHeatmapClip()
    );
    this.#progressObserver.observe(progressEl);
  }

  /**
   * Touch has no hover, so a marker's own pointerenter label never shows. While a finger
   * (or pen tip) is down on the bar, name the moment it is passing over, next to the
   * chapter and time media-chrome already shows. Mouse is left alone: hovering a marker
   * keeps driving the preview through its own pointerenter/leave.
   *
   * The position comes from the pointer, not `mediapreviewtime`: media-chrome only
   * refreshes that once it moves a second or more, too coarse to land on a marker.
   * media-chrome's own drag state is not consulted: its window listener runs after this
   * one, so it would lag the first move. The gesture ends on a window-level pointerup or
   * pointercancel, since a pen dragged off the bar never reports either to this host.
   */
  #onScrubMove(evt) {
    // media-chrome's own rule for "the finger is down": touch has no `buttons` guarantee
    const inContact = evt.pointerType === 'touch' || (evt.buttons & 1);
    if (evt.pointerType === 'mouse' || !evt.isPrimary || !inContact) return;
    if (!this._moments?.length) return;

    const rect = this.shadowRoot?.getElementById('container')
      ?.getBoundingClientRect();
    const bounds = this.#timelineBounds();
    if (!rect?.width || !bounds) return;

    // Same geometry the markers are placed with (see #renderMoments)
    const ratio = Math.max(
      0,
      Math.min(1, (evt.clientX - rect.left) / rect.width),
    );
    const span = bounds.end - bounds.start;
    const moment = findMomentNear(
      this._moments,
      bounds.start + ratio * span,
      toleranceSecondsForBar(span, rect.width),
      bounds,
    );
    const label = moment ? getMomentLabel(moment) : undefined;

    this.#scrubPreviewActive = true;
    if (label) this.#showMomentPreview(label);
    else this.#clearMomentPreview();
  }

  #endScrubPreview() {
    if (!this.#scrubPreviewActive) return;
    this.#scrubPreviewActive = false;
    this.#clearMomentPreview();
  }

  #showMomentPreview(label) {
    const display = this.shadowRoot?.querySelector('#moment-label');
    if (!display) return;
    if (!label) {
      this.#clearMomentPreview();
      return;
    }
    if (!display.hidden && display.textContent === label) return;
    display.textContent = label;
    display.hidden = false;
  }

  #clearMomentPreview() {
    const display = this.shadowRoot?.querySelector('#moment-label');
    if (display) {
      display.hidden = true;
      display.textContent = '';
    }
  }
}

if (!customElements.get('bunny-media-time-range')) {
  customElements.define('bunny-media-time-range', BunnyMediaTimeRange);
}

export default BunnyMediaTimeRange;
